import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, Check, Crown, Trash2, LogOut, User as UserIcon, Users, Lock, Bookmark } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { notify } from "../lib/notify";

export function Profile() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [searches, setSearches] = useState([]);
  useEffect(() => {
    api.get("/assessments").then(r => setItems(r.data.assessments)).catch(() => {});
    api.get("/searches").then(r => setSearches(r.data.searches)).catch(() => {});
  }, []);

  if (!user) return null;

  const remove = async (id) => {
    if (!window.confirm("Delete this saved assessment?")) return;
    try {
      await api.delete(`/assessments/${id}`);
      setItems(prev => prev.filter(i => i.id !== id));
      notify.success("Assessment deleted.");
    } catch { notify.error("Could not delete."); }
  };

  const toggleShare = async (a) => {
    const share = a.visibility !== "team";
    try {
      await api.post(`/assessments/${a.id}/share`, { share_with_team: share });
      setItems(prev => prev.map(i => i.id === a.id ? { ...i, visibility: share ? "team" : "private" } : i));
      notify.success(share ? "Shared with your team." : "Made private.");
    } catch (e) { notify.error(e.response?.data?.detail || "Could not update sharing."); }
  };

  const removeSearch = async (id) => {
    try {
      await api.delete(`/searches/${id}`);
      setSearches(prev => prev.filter(s => s.id !== id));
      notify.success("Search deleted.");
    } catch { notify.error("Could not delete."); }
  };

  const toggleSearchShare = async (s) => {
    const share = s.visibility !== "team";
    try {
      await api.post(`/searches/${s.id}/share`, { share_with_team: share });
      setSearches(prev => prev.map(i => i.id === s.id ? { ...i, visibility: share ? "team" : "private" } : i));
      notify.success(share ? "Shared with your team." : "Made private.");
    } catch (e) { notify.error(e.response?.data?.detail || "Could not update sharing."); }
  };

  return (
    <div data-testid="page-profile">
      <section className="border-b border-slate-200">
        <div className="container-rp py-12 lg:py-16 flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="overline">Your account</div>
            <h1 className="font-display text-4xl lg:text-5xl font-bold mt-3 tracking-tight">Hi, {user.name || user.email}.</h1>
            <div className="mt-3 flex items-center gap-3 text-sm text-slate-500">
              <span>{user.email}</span>
              <span className="text-[10px] font-mono px-2 py-0.5 rounded-sm" style={{ background: "var(--rp-bg-subtle)", border: "1px solid var(--rp-border)" }}>{user.role}</span>
              {user.is_pro && <span className="text-[10px] font-mono px-2 py-0.5 rounded-sm text-white" style={{ background: "var(--rp-accent)" }}><Crown size={10} className="inline mr-1" />Pro</span>}
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            {!user.is_pro && <Link to="/pricing" className="btn-accent">Upgrade to Pro <ArrowRight size={14} /></Link>}
            {user.role === "admin" && <Link to="/admin" className="btn-outline">Admin panel</Link>}
            <button onClick={() => { logout(); nav("/"); }} className="btn-outline" data-testid="profile-logout"><LogOut size={14} /> Sign out</button>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container-rp">
          <div className="overline">Saved assessments</div>
          <h2 className="font-display text-3xl font-semibold mt-3">{items.length} saved</h2>

          {items.length === 0 ? (
            <div className="rp-card p-10 mt-8 text-center">
              <p className="text-slate-600">You haven't saved any assessments yet.</p>
              <div className="mt-6 flex justify-center gap-3 flex-wrap">
                <Link to="/tools/csrs-calculator" className="btn-accent">Run CSRS</Link>
                <Link to="/tools/pdpa-scorer" className="btn-outline">PDPA Scorer</Link>
                <Link to="/tools/mas-trm-readiness" className="btn-outline">MAS TRM</Link>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200 mt-8">
              {items.map(a => (
                <AssessmentCard key={a.id} a={a} canShare={!!user.org_id} onDelete={remove} onToggleShare={toggleShare} />
              ))}
            </div>
          )}

          {/* Saved searches */}
          <div className="mt-16">
            <div className="overline flex items-center gap-2"><Bookmark size={11} /> Saved searches</div>
            <h2 className="font-display text-3xl font-semibold mt-3">{searches.length} saved</h2>
            {searches.length === 0 ? (
              <p className="text-slate-500 mt-4 text-sm">Use the global search (magnifier in the nav) and hit <strong>Save search</strong> to keep Claude's answer here.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-slate-200 border border-slate-200 mt-8">
                {searches.map(s => (
                  <SavedSearchCard key={s.id} s={s} canShare={!!user.org_id} onDelete={removeSearch} onToggleShare={toggleSearchShare} />
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function ShareToggle({ shared, onClick, testid }) {
  return (
    <button onClick={onClick} className="text-xs hover:underline inline-flex items-center gap-1" style={{ color: shared ? "var(--rp-accent)" : "" }} data-testid={testid}>
      {shared ? <><Users size={12} /> Shared · make private</> : <><Lock size={12} /> Share with team</>}
    </button>
  );
}

function AssessmentCard({ a, canShare, onDelete, onToggleShare }) {
  return (
    <div className="rp-surface p-6" data-testid={`saved-${a.id}`}>
      <div className="overline-muted">{a.type}</div>
      <div className="font-display text-lg font-semibold mt-2">{a.title}</div>
      <div className="text-[10px] font-mono text-slate-400 mt-1">{new Date(a.created_at).toLocaleString()}</div>
      {a.result?.score != null && (
        <div className="font-display text-3xl font-bold mt-4" style={{ color: "var(--rp-accent)" }}>{a.result.score}<span className="text-sm text-slate-400">/100</span></div>
      )}
      {a.result?.band && <div className="text-xs text-slate-500 mt-1">{a.result.band}</div>}
      {a.result?.level_name && <div className="text-xs text-slate-500 mt-1">Level: {a.result.level_name}</div>}
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button onClick={() => onDelete(a.id)} className="text-xs text-rose-600 hover:underline inline-flex items-center gap-1" data-testid={`del-${a.id}`}><Trash2 size={12} /> Delete</button>
        {canShare && <ShareToggle shared={a.visibility === "team"} onClick={() => onToggleShare(a)} testid={`share-assessment-${a.id}`} />}
      </div>
    </div>
  );
}

function SavedSearchCard({ s, canShare, onDelete, onToggleShare }) {
  return (
    <div className="rp-surface p-6" data-testid={`saved-search-${s.id}`}>
      <div className="font-display text-base font-semibold">"{s.query}"</div>
      <div className="text-[10px] font-mono text-slate-400 mt-1">{new Date(s.created_at).toLocaleString()}</div>
      {s.answer && <p className="text-xs text-slate-600 mt-3 leading-relaxed line-clamp-3">{s.answer}</p>}
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button onClick={() => onDelete(s.id)} className="text-xs text-rose-600 hover:underline inline-flex items-center gap-1" data-testid={`del-search-${s.id}`}><Trash2 size={12} /> Delete</button>
        {canShare && <ShareToggle shared={s.visibility === "team"} onClick={() => onToggleShare(s)} testid={`share-search-${s.id}`} />}
      </div>
    </div>
  );
}

export function Pricing() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [pkgs, setPkgs] = useState([]);
  const [busy, setBusy] = useState(null);
  const [coupon, setCoupon] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponBusy, setCouponBusy] = useState(false);

  useEffect(() => { api.get("/pricing/packages").then(r => setPkgs(r.data.packages)); }, []);

  const applyCoupon = async () => {
    if (!coupon.trim()) return;
    setCouponBusy(true);
    try {
      const r = await api.get(`/coupons/validate/${coupon.trim().toUpperCase()}`);
      setAppliedCoupon(r.data);
      notify.success(`Coupon ${r.data.code} applied — ${r.data.percent_off ? `${r.data.percent_off}% off` : `S$${r.data.amount_off_sgd} off`}.`);
    } catch (e) {
      setAppliedCoupon(null);
      notify.error(e.response?.data?.detail || "Invalid coupon.");
    } finally { setCouponBusy(false); }
  };

  const previewPrice = (p) => {
    if (!appliedCoupon) return p.amount;
    const scope = appliedCoupon.applies_to_packages;
    if (scope?.length && !scope.includes(p.id)) return p.amount;
    if (appliedCoupon.percent_off) return Math.round(p.amount * (1 - appliedCoupon.percent_off / 100) * 100) / 100;
    if (appliedCoupon.amount_off_sgd) return Math.max(0.5, p.amount - appliedCoupon.amount_off_sgd);
    return p.amount;
  };

  const checkout = async (pkgId) => {
    if (!user) { nav(`/login?next=/pricing`); return; }
    setBusy(pkgId);
    try {
      const payload = { package_id: pkgId, origin_url: window.location.origin };
      if (appliedCoupon) payload.coupon_code = appliedCoupon.code;
      const r = await api.post("/payments/checkout", payload);
      window.location.href = r.data.url;
    } catch (e) {
      notify.error("Could not start checkout. Please try again.");
      setBusy(null);
    }
  };

  return (
    <div data-testid="page-pricing">
      <section className="border-b border-slate-200">
        <div className="container-rp py-16 lg:py-24">
          <div className="overline">Pricing · SGD</div>
          <h1 className="font-display text-5xl lg:text-6xl font-bold mt-3 tracking-tight">Free for the basics.<br /><span style={{ color: "var(--rp-accent)" }}>Pro</span> for the templates.</h1>
          <p className="text-slate-600 mt-4 max-w-2xl text-lg">All scoring tools and the Singapore Hub stay free, forever. Pro unlocks the 12-template library, unlimited saved assessments, and deeper AI Advisor sessions.</p>
          {/* Coupon input */}
          <div className="mt-8 max-w-md">
            <label className="text-xs uppercase tracking-widest text-slate-500 font-mono">Have a coupon?</label>
            <div className="flex gap-2 mt-2">
              <input value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} placeholder="LAUNCH50" className="flex-1 border border-slate-200 rounded-sm px-3 py-2 text-sm font-mono" data-testid="pricing-coupon-input" />
              <button onClick={applyCoupon} disabled={couponBusy || !coupon.trim()} className="btn-outline text-sm" data-testid="pricing-coupon-apply">{couponBusy ? "…" : "Apply"}</button>
            </div>
            {appliedCoupon && <div className="text-xs text-emerald-600 mt-2 font-mono" data-testid="coupon-applied">✓ {appliedCoupon.code} · {appliedCoupon.percent_off ? `${appliedCoupon.percent_off}% off` : `S$${appliedCoupon.amount_off_sgd} off`}</div>}
          </div>
          <div className="mt-6"><Link to="/enterprise" className="text-sm font-medium" style={{ color: "var(--rp-accent)" }}>Need 25+ seats? See Enterprise →</Link></div>
        </div>
      </section>

      <section className="section">
        <div className="container-rp grid grid-cols-1 md:grid-cols-3 gap-6">
          {pkgs.map(p => (
            <PricingTierCard key={p.id} p={p} discounted={previewPrice(p)} isPro={!!user?.is_pro} busy={busy === p.id} onBuy={() => checkout(p.id)} />
          ))}
        </div>
        <p className="text-center text-xs text-slate-500 mt-10">Stripe test mode. SGD currency. Cancel anytime in your account.</p>
      </section>
    </div>
  );
}

function PricingTierCard({ p, discounted, isPro, busy, onBuy }) {
  const hasDiscount = discounted < p.amount;
  return (
    <div className="rp-card p-8 flex flex-col" data-testid={`pkg-${p.id}`}>
      <div className="overline">{p.interval}</div>
      <div className="font-display text-2xl font-semibold mt-3">{p.label}</div>
      <div className="mt-5 flex items-baseline gap-2">
        <div className="font-display text-5xl font-bold" style={{ color: "var(--rp-primary)" }}>S${discounted}</div>
        <div className="text-sm text-slate-500">/ {p.interval}</div>
        {hasDiscount && <div className="text-sm text-slate-400 line-through ml-2">S${p.amount}</div>}
      </div>
      <ul className="mt-6 space-y-2 text-sm flex-1">
        {p.benefits.map(b => (
          <li key={b} className="flex items-start gap-2"><Check size={14} style={{ color: "var(--rp-accent)" }} className="mt-0.5 flex-shrink-0" /> {b}</li>
        ))}
      </ul>
      <button onClick={onBuy} disabled={busy || isPro} className="btn-accent mt-6 justify-center disabled:opacity-50" data-testid={`buy-${p.id}`}>
        {isPro ? "Already Pro" : busy ? "Redirecting…" : "Get started"} <ArrowRight size={14} />
      </button>
    </div>
  );
}

export function PricingSuccess() {
  const [params] = useSearchParams();
  const [status, setStatus] = useState("polling");
  const { refresh } = useAuth();
  const sessionId = params.get("session_id");
  const [tries, setTries] = useState(0);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let cancelled = false;
    const poll = async (n) => {
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          if (!cancelled) { setStatus("paid"); refresh(); }
          return;
        }
        if (r.data.status === "expired") { if (!cancelled) setStatus("expired"); return; }
        if (n >= 8) { if (!cancelled) setStatus("pending"); return; }
        setTimeout(() => { setTries(n + 1); poll(n + 1); }, 2000);
      } catch { if (!cancelled) setStatus("error"); }
    };
    poll(0);
    return () => { cancelled = true; };
  }, [sessionId, refresh]);

  return (
    <div data-testid="page-pricing-success">
      <section className="section">
        <div className="container-rp max-w-2xl text-center">
          {status === "polling" && <>
            <div className="overline">Confirming payment</div>
            <h1 className="font-display text-4xl font-bold mt-3">Just a moment…</h1>
            <p className="text-slate-600 mt-3">Verifying your subscription with Stripe (attempt {tries + 1}/9).</p>
          </>}
          {status === "paid" && <>
            <div className="overline" style={{ color: "var(--rp-accent)" }}>You're in.</div>
            <h1 className="font-display text-5xl font-bold mt-3">Welcome to Pro.</h1>
            <p className="text-slate-600 mt-3 max-w-md mx-auto">All 12 templates unlocked. Saved assessments unlimited. AI Advisor depth boosted.</p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link to="/singapore/templates" className="btn-accent">Open templates library <ArrowRight size={14} /></Link>
              <Link to="/profile" className="btn-outline">View account</Link>
            </div>
          </>}
          {status === "pending" && <>
            <div className="overline">Still processing</div>
            <h1 className="font-display text-4xl font-bold mt-3">Payment pending</h1>
            <p className="text-slate-600 mt-3">Stripe is still confirming. Check back in a few minutes — your Pro access will activate automatically.</p>
            <Link to="/profile" className="btn-outline mt-8">Back to profile</Link>
          </>}
          {status === "expired" && <>
            <h1 className="font-display text-4xl font-bold mt-3">Session expired</h1>
            <Link to="/pricing" className="btn-accent mt-8">Try again</Link>
          </>}
          {status === "error" && <>
            <h1 className="font-display text-4xl font-bold mt-3">Could not confirm</h1>
            <p className="text-slate-600 mt-3">Please refresh, or contact us if you were charged.</p>
            <Link to="/profile" className="btn-outline mt-8">Back to profile</Link>
          </>}
        </div>
      </section>
    </div>
  );
}

export function PricingCancel() {
  return (
    <section className="section">
      <div className="container-rp max-w-2xl text-center" data-testid="page-pricing-cancel">
        <div className="overline">Payment cancelled</div>
        <h1 className="font-display text-4xl font-bold mt-3">No charge made.</h1>
        <p className="text-slate-600 mt-3">You cancelled the checkout. Free features remain available.</p>
        <div className="mt-8 flex justify-center gap-3 flex-wrap">
          <Link to="/pricing" className="btn-accent">Back to pricing</Link>
          <Link to="/" className="btn-outline">Home</Link>
        </div>
      </div>
    </section>
  );
}
